import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  ClipboardCheck, Sparkles, Loader2, ChevronDown, ChevronRight,
  CheckCircle, Circle, FileText, Download, Printer, Save,
  AlertTriangle, Target, Shield, Settings
} from "lucide-react";

const AuditWorkflowPage = () => {
  const { t } = useTranslation('audit');
  const [step, setStep] = useState("setup"); // setup | generating | checklist
  const [formData, setFormData] = useState({
    clientName: "",
    industry: "life-sciences",
    standards: [],
    scope: "",
    auditType: "gap-assessment",
  });
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState([]);
  const [checkStates, setCheckStates] = useState({});
  const [notes, setNotes] = useState({});
  const printRef = useRef(null);

  const industries = [
    { id: "life-sciences", label: "Life Sciences / Pharma" },
    { id: "automotive", label: "Automotive" },
    { id: "aerospace", label: "Aerospace & Defense" },
    { id: "industrial", label: "Industrial Manufacturing" },
    { id: "iot", label: "IoT / Technology" },
    { id: "medical-devices", label: "Medical Devices" },
  ];

  const standardOptions = {
    "life-sciences": ["ISO 9001", "FDA 21 CFR 210/211", "ICH Q7", "ICH Q10", "EU GMP Annex 1", "ISO 13485"],
    "automotive": ["IATF 16949", "VDA 6.3", "ISO 9001", "AIAG Core Tools", "CQI Standards"],
    "aerospace": ["AS9100 Rev D", "AS9102", "NADCAP", "DO-178C", "ISO 9001"],
    "industrial": ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 50001"],
    "iot": ["ISO 27001", "ISO 9001", "IEC 62443", "NIST Cybersecurity"],
    "medical-devices": ["ISO 13485", "FDA 21 CFR 820", "EU MDR 2017/745", "ISO 14971"],
  };

  const auditTypes = [
    { id: "gap-assessment", label: "Gap Assessment", desc: "Identify gaps against standard requirements" },
    { id: "compliance-audit", label: "Compliance Audit", desc: "Full compliance verification audit" },
    { id: "supplier-audit", label: "Supplier Audit", desc: "Supplier qualification/surveillance audit" },
    { id: "process-audit", label: "Process Audit", desc: "Process-focused audit (VDA 6.3 style)" },
  ];

  const toggleStandard = (std) => {
    setFormData((prev) => ({
      ...prev,
      standards: prev.standards.includes(std)
        ? prev.standards.filter((s) => s !== std)
        : [...prev.standards, std],
    }));
  };

  const toggleSection = (idx) => {
    setExpandedSections((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const toggleCheck = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setCheckStates((prev) => ({
      ...prev,
      [key]: prev[key] === "pass" ? "fail" : prev[key] === "fail" ? "na" : prev[key] === "na" ? undefined : "pass",
    }));
  };

  const updateNote = (sectionIdx, itemIdx, text) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setNotes((prev) => ({ ...prev, [key]: text }));
  };

  const generateWorkflow = async () => {
    if (!formData.clientName || formData.standards.length === 0) return;

    setLoading(true);
    setStep("generating");

    try {
      const prompt = `Generate a detailed ${formData.auditType === "gap-assessment" ? "gap assessment" : formData.auditType === "compliance-audit" ? "compliance audit" : formData.auditType === "supplier-audit" ? "supplier qualification audit" : "process audit"} checklist for:

Client: ${formData.clientName}
Industry: ${industries.find((i) => i.id === formData.industry)?.label}
Standards: ${formData.standards.join(", ")}
Scope: ${formData.scope || "Full system"}

Generate a structured JSON response with this exact format (no markdown, just raw JSON):
{
  "title": "Audit title",
  "summary": "Brief description",
  "sections": [
    {
      "name": "Section name (e.g., Quality Management System)",
      "reference": "Standard clause reference",
      "items": [
        {
          "id": "1.1",
          "question": "Specific audit question",
          "evidence": "What evidence to look for",
          "criteria": "Pass/fail criteria",
          "risk": "high|medium|low"
        }
      ]
    }
  ]
}

Generate 6-8 sections with 4-6 items each. Be specific to the standards and industry. Include clause references.`;

      const res = await axios.post(
        `${API}/ai/chat`,
        { message: prompt, context: formData.industry },
        { withCredentials: true }
      );

      // Check if response is async job
      if (res.data.job_id) {
        // Poll for result
        const jobId = res.data.job_id;
        let attempts = 0;
        const maxAttempts = 30; // 30 * 3s = 90s max
        const poll = async () => {
          while (attempts < maxAttempts) {
            await new Promise(r => setTimeout(r, 3000));
            attempts++;
            try {
              const jobRes = await axios.get(`${API}/ai/jobs/${jobId}`, { withCredentials: true });
              if (jobRes.data.status === "completed" && jobRes.data.response) {
                return jobRes.data.response;
              } else if (jobRes.data.status === "failed") {
                throw new Error(jobRes.data.error || "Generation failed");
              }
            } catch (pollErr) {
              if (pollErr.response?.status === 404) continue;
              throw pollErr;
            }
          }
          throw new Error("Generation timed out");
        };
        const aiResponse = await poll();
        parseAndSetChecklist(aiResponse);
      } else if (res.data.response) {
        parseAndSetChecklist(res.data.response);
      } else {
        setStep("setup");
      }
    } catch (err) {
      console.error("Failed to generate workflow:", err);
      setStep("setup");
    } finally {
      setLoading(false);
    }
  };

  const parseAndSetChecklist = (responseText) => {
    let parsed;
    try {
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        parsed = JSON.parse(jsonMatch[0]);
      }
    } catch (e) {
      try {
        parsed = JSON.parse(responseText);
      } catch (e2) {
        parsed = null;
      }
    }
    if (parsed && parsed.sections) {
      setChecklist(parsed);
      setExpandedSections([0]);
      setStep("checklist");
    } else {
      setStep("setup");
    }
  };

  const getProgress = () => {
    if (!checklist) return { total: 0, checked: 0, pass: 0, fail: 0, na: 0 };
    let total = 0, pass = 0, fail = 0, na = 0;
    checklist.sections.forEach((s, si) => {
      s.items.forEach((_, ii) => {
        total++;
        const state = checkStates[`${si}-${ii}`];
        if (state === "pass") pass++;
        else if (state === "fail") fail++;
        else if (state === "na") na++;
      });
    });
    return { total, checked: pass + fail + na, pass, fail, na };
  };

  const exportReport = () => {
    if (!checklist) return;
    const progress = getProgress();
    let report = `# ${checklist.title}\n`;
    report += `${checklist.summary}\n\n`;
    report += `Client: ${formData.clientName}\n`;
    report += `Date: ${new Date().toLocaleDateString()}\n`;
    report += `Progress: ${progress.checked}/${progress.total} items reviewed\n`;
    report += `Results: ${progress.pass} Pass | ${progress.fail} Fail | ${progress.na} N/A\n\n`;

    checklist.sections.forEach((section, si) => {
      report += `## ${section.name}\nRef: ${section.reference}\n\n`;
      section.items.forEach((item, ii) => {
        const key = `${si}-${ii}`;
        const status = checkStates[key] || "Not reviewed";
        const note = notes[key] || "";
        report += `### ${item.id} - ${item.question}\n`;
        report += `Evidence: ${item.evidence}\n`;
        report += `Status: ${status.toUpperCase()}\n`;
        if (note) report += `Notes: ${note}\n`;
        report += `\n`;
      });
    });

    const blob = new Blob([report], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${formData.clientName.replace(/\s+/g, "_")}_audit_report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const progress = getProgress();
  const riskColors = { high: "#F87171", medium: "#FBBF24", low: "#4ADE80" };
  const statusIcons = {
    pass: <CheckCircle size={18} className="text-[#4ADE80]" />,
    fail: <AlertTriangle size={18} className="text-[#F87171]" />,
    na: <Circle size={18} className="text-[#8B95A5]" />,
  };

  return (
    <QMSLayout title={t('audit:workflow.title')}>
      {step === "setup" && (
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h2 className="font-['Syne'] text-2xl font-bold text-white mb-2">
              AI-Powered Audit Workflow
            </h2>
            <p className="text-[#8B95A5]">
              Generate a comprehensive audit checklist based on your client's industry and compliance requirements
            </p>
          </div>

          <div className="space-y-6">
            {/* Client Name */}
            <div>
              <label className="block text-[#CBD5E1] text-sm font-medium mb-2">{t('workflow.clientName')}</label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                className="qms-input"
                placeholder="e.g., PharmaCorp Ltd"
                data-testid="audit-client-name"
              />
            </div>

            {/* Industry */}
            <div>
              <label className="block text-[#CBD5E1] text-sm font-medium mb-2">{t('workflow.industrySector')}</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {industries.map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => setFormData({ ...formData, industry: ind.id, standards: [] })}
                    className={`p-3 rounded-[3px] text-sm text-left transition-all ${
                      formData.industry === ind.id
                        ? "bg-[#36A9E1] bg-opacity-15 border border-[#36A9E1] text-[#38BDF8]"
                        : "bg-[#1F222D] border border-[rgba(255,255,255,0.08)] text-[#8B95A5] hover:border-[#2D313D]"
                    }`}
                    data-testid={`audit-industry-${ind.id}`}
                  >
                    {ind.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Standards */}
            <div>
              <label className="block text-[#CBD5E1] text-sm font-medium mb-2">{t('workflow.applicableStandards')}</label>
              <div className="flex flex-wrap gap-2">
                {(standardOptions[formData.industry] || []).map((std) => (
                  <button
                    key={std}
                    onClick={() => toggleStandard(std)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      formData.standards.includes(std)
                        ? "bg-[#E8A838] bg-opacity-20 border border-[#E8A838] text-[#FBBF24]"
                        : "bg-[#1F222D] border border-[rgba(255,255,255,0.08)] text-[#8B95A5] hover:border-[#2D313D]"
                    }`}
                    data-testid={`audit-std-${std.replace(/\s+/g, "-")}`}
                  >
                    {std}
                  </button>
                ))}
              </div>
            </div>

            {/* Audit Type */}
            <div>
              <label className="block text-[#CBD5E1] text-sm font-medium mb-2">Audit Type</label>
              <div className="grid grid-cols-2 gap-2">
                {auditTypes.map((at) => (
                  <button
                    key={at.id}
                    onClick={() => setFormData({ ...formData, auditType: at.id })}
                    className={`p-3 rounded-[3px] text-left transition-all ${
                      formData.auditType === at.id
                        ? "bg-[#36A9E1] bg-opacity-15 border border-[#36A9E1]"
                        : "bg-[#1F222D] border border-[rgba(255,255,255,0.08)] hover:border-[#2D313D]"
                    }`}
                    data-testid={`audit-type-${at.id}`}
                  >
                    <span className={`text-sm font-medium ${formData.auditType === at.id ? "text-[#38BDF8]" : "text-[#CBD5E1]"}`}>
                      {at.label}
                    </span>
                    <p className="text-[#8B95A5] text-xs mt-0.5">{at.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Scope */}
            <div>
              <label className="block text-[#CBD5E1] text-sm font-medium mb-2">Audit Scope (optional)</label>
              <textarea
                value={formData.scope}
                onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                className="qms-input h-24 resize-none"
                placeholder="e.g., Focus on production area, incoming inspection, and document control processes"
                data-testid="audit-scope"
              />
            </div>

            {/* Generate Button */}
            <button
              onClick={generateWorkflow}
              disabled={!formData.clientName || formData.standards.length === 0}
              className="btn-primary w-full py-4 disabled:opacity-40 justify-center"
              data-testid="generate-workflow-btn"
            >
              <Sparkles size={18} />
              {t('workflow.generateWorkflow')}
            </button>
          </div>
        </div>
      )}

      {step === "generating" && (
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 size={48} className="text-[#36A9E1] animate-spin mb-6" />
          <h3 className="font-['Syne'] text-xl font-bold text-white mb-2">{t('workflow.generatingTitle')}</h3>
          <p className="text-[#8B95A5] text-center max-w-md">
            AI is analyzing {formData.standards.join(", ")} requirements for {formData.clientName}...
          </p>
        </div>
      )}

      {step === "checklist" && checklist && (
        <div ref={printRef}>
          {/* Header */}
          <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
            <div>
              <h2 className="font-['Syne'] text-xl font-bold text-white mb-1">{checklist.title}</h2>
              <p className="text-[#8B95A5] text-sm">{checklist.summary}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-[#8B95A5]">
                <span>Client: <span className="text-[#CBD5E1]">{formData.clientName}</span></span>
                <span>Date: <span className="text-[#CBD5E1]">{new Date().toLocaleDateString()}</span></span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep("setup")} className="btn-ghost py-2 px-3 text-xs" data-testid="new-audit-btn">
                <Settings size={14} /> New Audit
              </button>
              <button onClick={exportReport} className="btn-primary py-2 px-3 text-xs" data-testid="export-report-btn">
                <Download size={14} /> Export Report
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="qms-card mb-6" data-testid="audit-progress">
            <div className="qms-card-body">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[#CBD5E1] text-sm font-medium">{t('workflow.auditProgress')}</span>
                <span className="text-[#8B95A5] text-sm">{progress.checked}/{progress.total} items</span>
              </div>
              <div className="progress-bar mb-3">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress.total ? (progress.checked / progress.total) * 100 : 0}%` }}
                />
              </div>
              <div className="flex gap-4 text-xs">
                <span className="flex items-center gap-1.5 text-[#4ADE80]">
                  <CheckCircle size={12} /> {progress.pass} Pass
                </span>
                <span className="flex items-center gap-1.5 text-[#F87171]">
                  <AlertTriangle size={12} /> {progress.fail} Fail
                </span>
                <span className="flex items-center gap-1.5 text-[#8B95A5]">
                  <Circle size={12} /> {progress.na} N/A
                </span>
              </div>
            </div>
          </div>

          {/* Checklist Sections */}
          <div className="space-y-3" data-testid="audit-checklist">
            {checklist.sections.map((section, si) => (
              <div key={si} className="qms-card overflow-hidden">
                <div
                  className="p-4 flex items-center gap-3 cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                  onClick={() => toggleSection(si)}
                >
                  <Target size={18} className="text-[#36A9E1] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Syne'] font-semibold text-white text-sm">{section.name}</h3>
                    <span className="text-[#8B95A5] text-xs">{section.reference}</span>
                  </div>
                  <span className="text-[#8B95A5] text-xs mr-2">{section.items.length} items</span>
                  {expandedSections.includes(si) ? (
                    <ChevronDown size={16} className="text-[#8B95A5]" />
                  ) : (
                    <ChevronRight size={16} className="text-[#8B95A5]" />
                  )}
                </div>

                {expandedSections.includes(si) && (
                  <div className="border-t border-[rgba(255,255,255,0.06)]">
                    {section.items.map((item, ii) => {
                      const key = `${si}-${ii}`;
                      const state = checkStates[key];
                      return (
                        <div
                          key={ii}
                          className={`p-4 border-b border-[rgba(255,255,255,0.04)] last:border-b-0 ${
                            state === "pass" ? "bg-[rgba(74,222,128,0.03)]" : state === "fail" ? "bg-[rgba(248,113,113,0.03)]" : ""
                          }`}
                          data-testid={`audit-item-${si}-${ii}`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Status Toggle */}
                            <button
                              onClick={() => toggleCheck(si, ii)}
                              className="mt-0.5 flex-shrink-0"
                              data-testid={`audit-check-${si}-${ii}`}
                            >
                              {state ? statusIcons[state] : <Circle size={18} className="text-[#2D313D]" />}
                            </button>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="text-[#CBD5E1] text-sm font-medium">
                                  <span className="text-[#8B95A5] mr-1.5">{item.id}</span>
                                  {item.question}
                                </span>
                                <span
                                  className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0"
                                  style={{
                                    color: riskColors[item.risk],
                                    backgroundColor: `${riskColors[item.risk]}15`,
                                  }}
                                >
                                  {item.risk}
                                </span>
                              </div>
                              <p className="text-[#8B95A5] text-xs mb-1">
                                <span className="text-[#6B7280]">Evidence:</span> {item.evidence}
                              </p>
                              <p className="text-[#8B95A5] text-xs mb-2">
                                <span className="text-[#6B7280]">Criteria:</span> {item.criteria}
                              </p>
                              {/* Notes */}
                              <textarea
                                value={notes[key] || ""}
                                onChange={(e) => updateNote(si, ii, e.target.value)}
                                placeholder="Add notes, findings, observations..."
                                className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.06)] rounded-[3px] p-2 text-[#CBD5E1] text-xs resize-none h-16 focus:border-[#36A9E1] focus:outline-none"
                                data-testid={`audit-note-${si}-${ii}`}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default AuditWorkflowPage;
